// All content for the UAE Travel & Relocation Guide (mocked — frontend only).
// Images sourced from Unsplash / Pexels.


// ---- Responsive 4K image helper -------------------------------------------
// Unsplash (imgix) + Pexels both resize at the edge. We request 16:9 crops so portrait
// originals (several are 3840x5760) aren't shipped at 5 MB to show a third of them.
const _isUnsplash = (u) => /images\.unsplash\.com/.test(u);
const _isPexels = (u) => /images\.pexels\.com/.test(u);
// Portrait originals (shot tall) need a crop to make a wide hero; landscape ones should be shown whole.
const PORTRAIT = new Set([
  'photo-1546412414-e1885259563a', // Jumeirah beach / Burj Al Arab
  'photo-1603350942002-35f088445d3c', // Dubai fountain
  'photo-1528702748617-c64d49f918af', // JBR beach
]);
const _isPortrait = (u) => [...PORTRAIT].some((id) => u.includes(id));
export const imgAt = (u, w, ratio = '16:9') => {
  const h = ratio === '16:9' ? Math.round((w * 9) / 16) : ratio === '21:9' ? Math.round((w * 9) / 21) : undefined;
  if (_isUnsplash(u)) {
    if (_isPortrait(u)) {
      // crop, but bias toward the top so skyline/horizon stays in frame rather than a centre slice
      return `${u}?auto=format,compress&fit=crop&crop=entropy&w=${w}&h=${h}&q=82&sharp=10`;
    }
    // landscape: deliver the FULL frame at this width, no cropping — object-fit: cover handles the rest
    return `${u}?auto=format,compress&fit=max&w=${w}&q=82&sharp=10`;
  }
  if (_isPexels(u)) {
    return `${u}?auto=compress&cs=tinysrgb&w=${w}`;
  }
  return u;
};
export const HERO_WIDTHS = [768, 1280, 1920, 2560, 3840];
export const heroSrcSet = (u, ratio = '16:9') => HERO_WIDTHS.map((w) => `${imgAt(u, w, ratio)} ${w}w`).join(', ');

export const IMAGES = {
  heroSkyline:
    'https://images.unsplash.com/flagged/photo-1559717865-a99cac1c95d8',
  dubaiAlt:
    'https://images.unsplash.com/photo-1512453979798-5ea266f8880c',
  palmJumeirah:
    'https://images.unsplash.com/photo-1679682598283-a1b88b136ed8',
  grandMosque:
    'https://images.unsplash.com/photo-1584551246679-0daf3d275d0f',
  grandMosquePool:
    'https://images.unsplash.com/photo-1512632578888-169bbbc64f33',
  souk:
    'https://images.unsplash.com/photo-1636924271402-d639875aa2bb',
  marinaDusk:
    'https://images.pexels.com/photos/30554306/pexels-photo-30554306.jpeg',
  beachBurjAlArab:
    'https://images.unsplash.com/photo-1546412414-e1885259563a',
  jbrBeach:
    'https://images.unsplash.com/photo-1528702748617-c64d49f918af',
  marinaDay:
    'https://images.unsplash.com/photo-1611577810610-642f8ac05c32',
  yachtGulf:
    'https://images.pexels.com/photos/30047576/pexels-photo-30047576.jpeg',
  desertDunes:
    'https://images.unsplash.com/photo-1520440718111-45fe694b330a',
  burjFountain:
    'https://images.unsplash.com/photo-1603350942002-35f088445d3c',
  mosqueWide:
    'https://images.unsplash.com/photo-1512632578888-169bbbc64f33',
  visaDocs:
    'https://images.unsplash.com/photo-1454496406107-dc34337da8d6',
  abuDhabi:
    'https://images.unsplash.com/photo-1668060742975-9a462159b0bd',
  sharjah:
    'https://images.unsplash.com/photo-1611479853417-80d5b7869ddf',
  rasAlKhaimah:
    'https://images.unsplash.com/photo-1542878447-e2b6df2526fa',
  fujairah:
    'https://images.unsplash.com/photo-1648814037760-1dd4d3169a26',
  ajman:
    'https://images.unsplash.com/photo-1593334305856-1c6281efaa18',
  ummAlQuwain:
    'https://images.unsplash.com/photo-1664900314549-877760f81e78',
};

export const NAV_LINKS = [
  { label: 'The Emirates', to: '/emirates' },
  { label: 'Travel', to: '/visas-entry' },
  { label: 'Hotels', to: '/hotels' },
  { label: 'Deals', to: '/deals' },
  { label: 'Living', to: '/living' },
  { label: 'Property', to: '/property' },
  { label: 'Business', to: '/business' },
];

export const STATS = [
  { value: '7', label: 'Emirates Covered' },
  { value: '120+', label: 'Guides & Articles' },
  { value: '2026', label: 'Updated This Year' },
  { value: '100%', label: 'Independent, Disclosed' },
];

export const EMIRATES = [
  {
    slug: 'dubai',
    number: '01',
    name: 'Dubai',
    tagline: 'Creek-side heritage, big-city landmarks and a coastline made for long evenings.',
    image: IMAGES.dubaiAlt,
    intro:
      'Dubai is the UAE most visitors meet first — and for good reason. In a single day you can wander the gold and spice souks of Deira, cross the Creek on a wooden abra for a dirham, and watch the Dubai Fountain dance beneath the Burj Khalifa.',
    bestFor: 'First-time visitors, nightlife, landmarks, beaches',
    gettingThere: 'Dubai International (DXB) — the main gateway, 15 min from Downtown',
    highlights: [
      { title: 'Burj Khalifa & Dubai Mall', body: 'The world’s tallest building and a mall big enough to get lost in — book the 124th-floor deck for sunset.' },
      { title: 'Old Dubai & the Creek', body: 'Deira’s souks, the textile district of Bur Dubai, and a two-dirham abra ride across the water.' },
      { title: 'Dubai Marina & JBR', body: 'A forest of towers around a yacht-filled marina, with a beachfront promenade for evening strolls.' },
      { title: 'Desert at the edge', body: 'A 45-minute drive and you’re in red dunes — dawn dune drives and evening Bedouin camps.' },
    ],
    tip: 'The Dubai Metro is spotless, cheap and air-conditioned. Buy a Nol card at any station and skip the taxi queues between the big sights.',
  },
  {
    slug: 'abu-dhabi',
    number: '02',
    name: 'Abu Dhabi',
    tagline: 'The capital — cultural institutions, Grand Mosque, and room to slow down.',
    image: IMAGES.abuDhabi,
    intro:
      'The UAE’s capital trades some of Dubai’s pace for space and culture. The Sheikh Zayed Grand Mosque, the Louvre Abu Dhabi and a corniche made for cycling give the city a calmer, more considered feel.',
    bestFor: 'Culture, museums, families, slower itineraries',
    gettingThere: 'Zayed International (AUH) — or a 90-minute drive from Dubai',
    highlights: [
      { title: 'Sheikh Zayed Grand Mosque', body: 'One of the world’s largest mosques — free to visit, with strict but simple dress rules.' },
      { title: 'Louvre Abu Dhabi', body: 'Jean Nouvel’s “rain of light” dome over a world-class art collection on Saadiyat Island.' },
      { title: 'The Corniche', body: 'An 8km waterfront of beaches, cafés and cycle paths facing the Gulf.' },
      { title: 'Yas Island', body: 'Ferrari World, Warner Bros. World and the F1 circuit for a family day out.' },
    ],
    tip: 'Visit the Grand Mosque after 4pm — the crowds thin, the light turns gold, and the white marble glows at sunset.',
  },
  {
    slug: 'sharjah',
    number: '03',
    name: 'Sharjah',
    tagline: 'Museums, markets and heritage — and the UAE’s cultural capital.',
    image: IMAGES.sharjah,
    intro:
      'Named the UAE’s cultural capital, Sharjah is where the country’s heritage is most carefully kept. Expect restored heart-of-Sharjah courtyards, serious museums, and a more traditional, family-oriented atmosphere.',
    bestFor: 'Heritage, museums, art, budget-conscious travellers',
    gettingThere: 'Sharjah International (SHJ) — or 30 min from central Dubai',
    highlights: [
      { title: 'Heart of Sharjah', body: 'A restored heritage district of wind-tower houses, courtyards and craft workshops.' },
      { title: 'Sharjah Art Foundation', body: 'Contemporary galleries spread across the old town — anchor of the Sharjah Biennial.' },
      { title: 'Blue Souk & Central Market', body: 'Carpets, gold and antiques under distinctive blue-tiled arches.' },
      { title: 'Al Noor Island', body: 'A butterfly house and sculpture park on a landscaped island in Khalid Lagoon.' },
    ],
    tip: 'Sharjah is dry — no alcohol is served anywhere in the emirate. Plan accordingly, and remember dress codes here are a notch more conservative.',
  },
  {
    slug: 'ras-al-khaimah',
    number: '04',
    name: 'Ras Al Khaimah',
    tagline: 'Jebel Jais mountains, the world’s longest zipline, and resort beaches.',
    image: IMAGES.rasAlKhaimah,
    intro:
      'RAK is the UAE’s adventure emirate. The Hajar Mountains rise behind long resort beaches, and Jebel Jais — the country’s highest peak — is home to the world’s longest zipline and genuinely cool mountain air in winter.',
    bestFor: 'Adventure, mountains, resort beaches, weekend escapes',
    gettingThere: 'About 45 min from Dubai by car — no major international airport',
    highlights: [
      { title: 'Jebel Jais Flight', body: 'The world’s longest zipline — 2.8km at speeds up to 150km/h over the mountains.' },
      { title: 'Jebel Jais summit road', body: 'A beautifully engineered switchback drive to viewpoints and a mountain restaurant.' },
      { title: 'Resort beaches', body: 'Quiet, wide beaches with large resorts — popular with families and couples.' },
      { title: 'Dhayah Fort', body: 'A hilltop fort with a sweeping view over date plantations and the coast.' },
    ],
    tip: 'Jebel Jais can be 10°C cooler than the coast — bring a layer in winter. Sunset from the summit viewing deck is the highlight of the drive.',
  },
  {
    slug: 'fujairah',
    number: '05',
    name: 'Fujairah',
    tagline: 'The east coast — Gulf of Oman diving, wadis, and a different landscape.',
    image: IMAGES.fujairah,
    intro:
      'Fujairah is the only emirate entirely on the east coast, facing the Gulf of Oman. The water is clearer, the diving is the best in the country, and the Hajar Mountains tumble down to the sea in a landscape unlike the rest of the UAE.',
    bestFor: 'Diving & snorkelling, wadis, quiet beaches, road trips',
    gettingThere: 'Around 90 min from Dubai across the mountains by car',
    highlights: [
      { title: 'Snoopy Island', body: 'The country’s most popular snorkelling and dive spot, off Sandy Beach.' },
      { title: 'Al Bidyah Mosque', body: 'The oldest known mosque in the UAE, a small mud-brick structure from the 15th century.' },
      { title: 'Wadi Wurayah', body: 'A protected mountain reserve with waterfalls and natural pools.' },
      { title: 'Friday Market', body: 'Roadside stalls of pottery, carpets and local honey on the mountain route.' },
    ],
    tip: 'Pack reef-safe sunscreen and your own mask — the east-coast reefs are shallow and easy to reach straight from the beach.',
  },
  {
    slug: 'ajman',
    number: '06',
    name: 'Ajman',
    tagline: 'A smaller, quieter base twenty minutes from Dubai at a fraction of the price.',
    image: IMAGES.ajman,
    intro:
      'The smallest emirate makes a surprisingly good base. Ajman’s beaches are clean and uncrowded, seafood by the corniche is excellent, and room rates sit well below Dubai while staying within easy reach of it.',
    bestFor: 'Budget stays, relaxed beach days, long weekends',
    gettingThere: 'About 25 min from Dubai by car along the coast',
    highlights: [
      { title: 'Ajman Corniche', body: 'A laid-back waterfront of beaches, cafés and evening strolls.' },
      { title: 'Ajman Museum', body: 'An 18th-century fort housing displays on pearl diving and Emirati life.' },
      { title: 'The fish market', body: 'A working harbour where the daily catch is sold and grilled on the spot.' },
      { title: 'Mangrove walks', body: 'Quiet creeks and birdlife just outside the town centre.' },
    ],
    tip: 'Base yourself in Ajman on a budget and day-trip into Dubai — you’ll pay far less per night and the drive is shorter than many Dubai suburbs.',
  },
  {
    slug: 'umm-al-quwain',
    number: '07',
    name: 'Umm Al Quwain',
    tagline: 'The quietest emirate — mangroves, old town, and almost no tourists.',
    image: IMAGES.ummAlQuwain,
    intro:
      'If you want the UAE at its most unhurried, Umm Al Quwain is it. Kayak through mangroves, wander an old town that feels frozen in time, and visit the archaeological island of Ad-Dur — all with barely another tourist in sight.',
    bestFor: 'Kayaking, nature, escaping the crowds, day trips',
    gettingThere: 'About 50 min from Dubai by car up the northern coast',
    highlights: [
      { title: 'Mangrove kayaking', body: 'Paddle through calm lagoons rich with herons, flamingos and sea life.' },
      { title: 'The old town', body: 'Watchtowers, a small fort and a sleepy waterfront that has changed little in decades.' },
      { title: 'Dreamland Aqua Park', body: 'A large, old-school water park popular with Emirati families.' },
      { title: 'Ad-Dur ruins', body: 'One of the largest pre-Islamic sites on the Gulf coast.' },
    ],
    tip: 'Go on a weekday for the mangroves — you may well have the lagoon entirely to yourself. Bring water; facilities are minimal.',
  },
];

export const HOME_START_CARDS = [
  {
    label: 'Visas & Entry',
    title: 'Do Australians need a visa for the UAE?',
    body: 'Visa-on-arrival rules, validity periods, and the exact documents immigration asks for — updated for 2026 entry requirements.',
    cta: 'Read the guide',
    to: '/visas-entry',
    image: IMAGES.visaDocs,
    alt: 'Passport and visa documents on a desk',
  },
  {
    label: 'Hotels',
    title: 'Where to stay: Dubai, Abu Dhabi & beyond',
    body: 'Honest, categorised picks by budget and neighbourhood — with direct booking links and real nightly price ranges.',
    cta: 'See hotel picks',
    to: '/hotels',
    image: IMAGES.marinaDusk,
    alt: 'Infinity pool overlooking Dubai Marina at dusk',
  },
  {
    label: 'Living in the UAE',
    title: 'Cost of living as an expat in 2026',
    body: 'Rent, schooling, healthcare and the real monthly budget for a single professional or a family of four.',
    cta: 'Run the numbers',
    to: '/living',
    image: IMAGES.palmJumeirah,
    alt: 'Aerial view of Palm Jumeirah Dubai',
  },
];

export const CULTURE_CARDS = [
  {
    label: 'Culture',
    title: 'Dress codes, Ramadan etiquette & public conduct',
    body: 'What’s expected in malls, mosques and public spaces — and what’s a complete myth.',
    cta: 'Read the etiquette guide',
    to: '/culture-customs',
    image: IMAGES.souk,
    alt: 'Traditional souk market in the UAE',
  },
  {
    label: 'Culture',
    title: 'Visiting the Sheikh Zayed Grand Mosque',
    body: 'Opening hours, dress requirements, and how to visit respectfully as a non-Muslim tourist.',
    cta: 'Plan your visit',
    to: '/culture-customs',
    image: IMAGES.grandMosque,
    alt: 'Sheikh Zayed Grand Mosque at sunset',
  },
];

// ---- Visas page ----
export const VISA_PAGE = {
  hero: IMAGES.visaDocs,
  label: 'Visas & Entry Requirements',
  title: 'UAE visa rules for Australian passport holders',
  subtitle:
    'Updated for 2026 entry requirements. Always double-checked against the official UAE government source before publishing.',
  meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 7 min read',
  shortAnswer:
    'Australian citizens travelling to the UAE for tourism do not need to apply for a visa in advance. You’ll receive a free visa-on-arrival, valid for 30 days, stamped directly into your passport at immigration. Different rules apply for employment, long-term residence, or business visa categories.',
  airport: [
    'A passport valid for at least 6 months beyond your arrival date',
    'At least one blank visa page',
    'A return or onward flight ticket (may be requested)',
    'Proof of accommodation (hotel booking or host address)',
    'Sufficient funds for your stay (rarely checked, but can be requested)',
  ],
  extending:
    'If 30 days isn’t enough, most visa-on-arrival entries can be extended once through the UAE GDRFA, or via a visa service offered by your airline or hotel, for a government processing fee. Overstaying attracts daily fines, so apply before your visa expires — not after.',
  insurance:
    'The UAE does not require proof of travel insurance for Australian tourist entries. That said, private healthcare in Dubai and Abu Dhabi is excellent but expensive without cover — a short hospital stay can run into five figures AUD.',
  faqs: [
    { q: 'Do Australian citizens need a visa to visit the UAE?', a: 'No advance visa application is required for standard tourism — Australian passport holders receive a free 30-day visa-on-arrival. Always verify against the official UAE government source before you fly, as entry policy can change.' },
    { q: 'How many blank passport pages and how much validity do I need?', a: 'At least 6 months of validity remaining and one blank page for the entry stamp.' },
    { q: 'Can I extend my UAE tourist visa past 30 days?', a: 'Yes — one extension is typically available through UAE immigration authorities or a hotel/airline visa service, for a government fee.' },
    { q: 'Do I need travel insurance to enter the UAE?', a: 'Not mandatory for entry, but strongly recommended given the cost of private healthcare for visitors.' },
  ],
};

// ---- Hotels page ----
export const HOTELS_PAGE = {
  hero: IMAGES.marinaDusk,
  label: 'Hotels & Where to Stay',
  title: 'Where to actually stay in the UAE',
  subtitle:
    'Real neighbourhood breakdowns and honest picks by budget — not a list of whoever paid the most for placement.',
  meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 9 min read',
  tiers: [
    {
      tier: 'Luxury',
      area: 'Dubai Marina & Palm Jumeirah',
      price: 'AUD $450–1,200+ / night (peak Nov–Mar)',
      body: 'If budget isn’t the constraint, these neighbourhoods deliver the skyline-and-infinity-pool UAE experience most people picture.',
      listings: [
        { name: 'Marina-view suite, Dubai Marina', desc: 'Walking distance to Marina Walk, JBR Beach, and the tram line.', price: 'AUD $620 / night', cta: 'Check availability on Booking.com' },
        { name: 'Beachfront resort, Palm Jumeirah', desc: 'Private beach access, multiple pools, UAE’s most photographed address.', price: 'AUD $890 / night', cta: 'Check availability on Agoda' },
      ],
    },
    {
      tier: 'Mid-range',
      area: 'Downtown Dubai & Abu Dhabi Corniche',
      price: 'AUD $180–420 / night',
      body: 'Downtown puts you walking distance from Burj Khalifa and Dubai Mall; the Abu Dhabi Corniche gives you beach, business district and museum access in one strip.',
      listings: [
        { name: 'Downtown tower, Burj Khalifa view', desc: 'Steps from Dubai Mall and the fountain show, on the metro line.', price: 'AUD $310 / night', cta: 'Check availability on Booking.com' },
        { name: 'Corniche hotel, Abu Dhabi', desc: 'Beach access across the road, walkable to the main museums.', price: 'AUD $240 / night', cta: 'Check availability on Agoda' },
      ],
    },
    {
      tier: 'Budget & longer stays',
      area: 'Bur Dubai, Deira & Sharjah',
      price: 'AUD $70–150 / night',
      body: 'The old city districts are where the souks, spice markets, and genuinely cheap, excellent food live. Apartment-hotels here are the better choice for stays over a week.',
      listings: [
        { name: 'Apartment-hotel, Bur Dubai', desc: 'Kitchenette and laundry, walkable to the Creek and abra station.', price: 'AUD $110 / night', cta: 'Check availability on Booking.com' },
        { name: 'Heritage-district stay, Deira', desc: 'Minutes from the gold and spice souks, great street food nearby.', price: 'AUD $85 / night', cta: 'Check availability on Agoda' },
      ],
    },
  ],
  faqs: [
    { q: 'Is Dubai Marina or Downtown Dubai better for first-time visitors?', a: 'Downtown, generally — it puts you closer to the icons (Burj Khalifa, Dubai Mall, Dubai Fountain) on foot. Marina suits a second trip once you’ve done the landmark circuit.' },
    { q: 'Do hotel prices include the UAE tourism dirham fee?', a: 'Usually not — most UAE hotels charge a per-night “Tourism Dirham Fee” (roughly AED 7–20, ~AUD 3–8) at checkout, separate from the room rate.' },
    { q: 'Is it cheaper to book directly or through a booking site?', a: 'It varies by property, but booking platforms with free cancellation generally give you better flexibility for the same or lower price than calling direct, especially outside peak season.' },
  ],
};

// ---- Living page ----
export const LIVING_PAGE = {
  hero: IMAGES.palmJumeirah,
  label: 'Living in the UAE',
  title: 'Cost of living as an expat in 2026',
  subtitle:
    'The real monthly numbers behind a move to the UAE — rent, schooling, healthcare and everyday life, for a single professional and a family of four.',
  meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 11 min read',
  budgets: [
    {
      profile: 'Single professional',
      total: 'AUD $4,200 – 6,500 / month',
      items: [
        { label: 'Rent (1-bed, good area)', value: 'AUD $1,800 – 2,800' },
        { label: 'Utilities & internet', value: 'AUD $250 – 400' },
        { label: 'Groceries & eating out', value: 'AUD $700 – 1,100' },
        { label: 'Transport (car or metro)', value: 'AUD $350 – 700' },
        { label: 'Health insurance', value: 'AUD $120 – 300' },
      ],
    },
    {
      profile: 'Family of four',
      total: 'AUD $9,500 – 16,000 / month',
      items: [
        { label: 'Rent (3-bed villa/apartment)', value: 'AUD $3,500 – 6,500' },
        { label: 'School fees (2 children)', value: 'AUD $2,500 – 5,000' },
        { label: 'Utilities & internet', value: 'AUD $450 – 700' },
        { label: 'Groceries & eating out', value: 'AUD $1,600 – 2,600' },
        { label: 'Health insurance (family)', value: 'AUD $450 – 1,000' },
      ],
    },
  ],
  notes: [
    { title: 'Tax-free salary', body: 'There is no personal income tax in the UAE. A headline salary converts almost entirely to take-home pay — the single biggest reason the maths works.' },
    { title: 'Rent is paid in cheques', body: 'Landlords traditionally ask for the year’s rent in one to four cheques. Budget for a large upfront outlay, plus a ~5% agency fee and a deposit.' },
    { title: 'Schooling is the big variable', body: 'International school fees range enormously. For families, this is usually the line item that decides which emirate — and which neighbourhood — you can afford.' },
    { title: 'Healthcare is private', body: 'Employers must provide health cover in most emirates, but check the tier. Private care is excellent and expensive; good insurance is non-negotiable.' },
  ],
  faqs: [
    { q: 'Is the UAE really tax-free for expats?', a: 'There is no personal income tax. Corporate tax applies to some businesses, and VAT of 5% applies to most goods and services, but salaries are not taxed.' },
    { q: 'How much rent should I budget as a single person?', a: 'A comfortable one-bedroom in a well-connected Dubai neighbourhood typically runs AUD $1,800–2,800 per month, usually paid in one to four cheques for the year.' },
    { q: 'Do I need a car?', a: 'In Dubai and Abu Dhabi you can manage on metro, taxis and ride-hailing, but many expats buy a car once they settle in the outer suburbs.' },
  ],
};

// ---- Culture page ----
export const CULTURE_PAGE = {
  hero: IMAGES.grandMosquePool,
  label: 'Culture & Practical Life',
  title: 'Respect the place, enjoy it more',
  subtitle:
    'Customs, etiquette, dress codes and the small things that make a trip — or a move — go smoothly.',
  meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 8 min read',
  sections: [
    { heading: 'Dress codes in public', body: 'The UAE is more relaxed than many expect, but modesty is appreciated. In malls, souks and government buildings, cover shoulders and knees. Beaches and pools are fine for swimwear; the walk there is not. Sharjah is noticeably more conservative than Dubai.' },
    { heading: 'Visiting a mosque', body: 'At the Sheikh Zayed Grand Mosque, women are given an abaya and headscarf if needed; men wear long trousers. Remove shoes before entering prayer halls, keep voices low, and avoid visiting during prayer times unless you are praying.' },
    { heading: 'During Ramadan', body: 'Eating, drinking and smoking in public during daylight hours is avoided out of respect. Many restaurants screen off daytime dining; evenings come alive with iftar. Visitors are welcome — simply be discreet during the day.' },
    { heading: 'Public conduct', body: 'Public displays of affection beyond hand-holding are frowned upon. Swearing and rude gestures can carry real penalties. Alcohol is served in licensed hotels and venues, but public drunkenness is taken seriously.' },
    { heading: 'Photography', body: 'Ask before photographing people, especially local women. Avoid shooting government buildings, military sites and airports.' },
  ],
  mythBusters: [
    { myth: 'Women must cover their hair everywhere.', truth: 'Only inside mosques. Day to day, a headscarf is not required for visitors or residents.' },
    { myth: 'You can’t drink alcohol anywhere.', truth: 'Alcohol is served in licensed hotels, bars and restaurants across most emirates (not Sharjah).' },
    { myth: 'Holding hands will get you arrested.', truth: 'Hand-holding for couples is fine. It’s overt public affection that’s discouraged.' },
  ],
};

// ---- Generic guide pages (Property, Business) ----
export const GUIDE_PAGES = {
  property: {
    hero: IMAGES.dubaiAlt,
    label: 'Property & Real Estate',
    title: 'Buying and renting property in the UAE',
    subtitle:
      'Freehold zones, the buying process, and what Australians should know before investing in UAE real estate.',
    meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 10 min read',
    sections: [
      { heading: 'Can foreigners own property?', body: 'Yes — in designated freehold areas, foreign nationals can buy, own and sell property outright. Popular freehold zones include Dubai Marina, Downtown Dubai, Palm Jumeirah and parts of Abu Dhabi’s Saadiyat and Yas islands.' },
      { heading: 'The buying process', body: 'Expect a reservation agreement and deposit (often 10%), a Memorandum of Understanding, and transfer at the Dubai Land Department. Budget around 4% DLD transfer fee plus agency and registration costs on top of the price.' },
      { heading: 'Renting as an expat', body: 'Annual leases are registered through Ejari in Dubai. Rent is typically paid in one to four cheques, with a 5% agency fee and a refundable deposit of around 5%.' },
      { heading: 'Property and residency', body: 'Buying property above certain value thresholds can qualify you for a renewable investor or Golden Visa — a major draw for long-term relocators.' },
    ],
    faqs: [
      { q: 'Does buying property give me residency?', a: 'Property above a set value can qualify you for an investor visa or the 10-year Golden Visa, subject to current thresholds and conditions.' },
      { q: 'What fees should I budget beyond the price?', a: 'Roughly a 4% Dubai Land Department transfer fee, plus agency commission (~2%) and registration and mortgage fees where applicable.' },
    ],
  },
  business: {
    hero: IMAGES.abuDhabi,
    label: 'Business & Work',
    title: 'Working and starting a business in the UAE',
    subtitle:
      'Free zones, mainland companies, work visas and the basics of doing business as an Australian in the Emirates.',
    meta: 'Written by the Emirates Guide editorial team · Updated October 2026 · 12 min read',
    sections: [
      { heading: 'Free zone vs mainland', body: 'Free zones allow 100% foreign ownership and simplified setup, ideal for consultancies and online businesses. Mainland licences let you trade directly in the local market and bid for government work, with their own ownership rules.' },
      { heading: 'Work visas & sponsorship', body: 'Most employees are sponsored by their employer, who arranges the residence visa and Emirates ID. The process includes a medical check and biometric registration — usually completed within a few weeks.' },
      { heading: 'Tax basics', body: 'There is no personal income tax. A 9% corporate tax applies to business profits above a threshold, and 5% VAT applies to most goods and services.' },
      { heading: 'Banking', body: 'Opening a corporate account requires your trade licence, shareholder documents and proof of address. Allow time — compliance checks can be thorough.' },
    ],
    faqs: [
      { q: 'Do I need a local partner to start a company?', a: 'Not in free zones, which allow 100% foreign ownership. Many mainland activities now also permit full foreign ownership, though some strategic sectors still have restrictions.' },
      { q: 'Is salary really tax-free?', a: 'Yes — there is no personal income tax on salaries. Corporate tax and VAT apply at the business level.' },
    ],
  },
};

export const FOOTER_LINKS = [
  {
    title: 'The Emirates',
    links: [
      { label: 'Dubai', to: '/emirates/dubai' },
      { label: 'Abu Dhabi', to: '/emirates/abu-dhabi' },
      { label: 'Sharjah', to: '/emirates/sharjah' },
      { label: 'Compare all seven', to: '/emirates' },
    ],
  },
  {
    title: 'Plan your trip',
    links: [
      { label: 'Visas & Entry', to: '/visas-entry' },
      { label: 'Hotels', to: '/hotels' },
      { label: 'Culture & Customs', to: '/culture-customs' },
    ],
  },
  {
    title: 'Relocating',
    links: [
      { label: 'Cost of Living', to: '/living' },
      { label: 'Property', to: '/property' },
      { label: 'Business & Work', to: '/business' },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'About & Disclaimer', to: '/about' },
      { label: 'Affiliate Disclosure', to: '/about' },
    ],
  },
];
